import React, { useState } from "react";
import { getRemitaPublicConfig } from "../services/api";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { FaArrowLeft } from "react-icons/fa";
import DashboardNavbar from "../components/layout/DashboardNavbar";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const loadRemitaScript = () =>
  new Promise((resolve, reject) => {
    if (window.RmPaymentEngine) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://login.remita.net/payment/v1/remita-pay-inline.bundle.js";
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load Remita script"));
    document.body.appendChild(script);
  });

export function FundWalletConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { amount, walletBalance } = location.state || {};

  const { user } = useAuth();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (!amount || !user) {
    navigate("/wallet/fund");
    return null;
  }

  const SERVICE_CHARGE = 100;
  const totalCharge = Number(amount) + SERVICE_CHARGE;

  const userFirstName = user.first_name || user.name?.split(" ")[0] || "User";
  const userLastName = user.last_name || user.name?.split(" ").slice(1).join(" ") || "";
  const userEmail = user.email || "no-email@provided.com";

  const handlePayWithRemita = async () => {
    setIsProcessing(true);
    setErrorMessage("");

    try {
      
      await loadRemitaScript();

      const initResponse = await fetch("/api/wallet/fund/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Number(amount),
          email: userEmail,
          firstName: userFirstName,
          lastName: userLastName,
          narration: "Wallet Funding",
          currency: "NGN",
          userId: user.id || user._id,
        }),
      });

      if (!initResponse.ok) {
        throw new Error("Failed to initialize wallet funding transaction");
      }

      const data = await initResponse.json();

      if (!data.success || !data.rrr || !data.reference) {
        throw new Error(data.message || "Invalid transaction response from server");
      }

      const remitaConfig = await getRemitaPublicConfig();
      const paymentEngine = window.RmPaymentEngine.init({
        key: remitaConfig.public_key,
        processRrr: true,
        transactionId: data.reference,
        customerId: userEmail,
        firstName: userFirstName,
        lastName: userLastName,
        email: userEmail,
        amount: Number(amount),
        narration: "Wallet Top-up",
        extendedData: {
          customFields: [
            { name: "rrr", value: data.rrr },
            { name: "user_id", value: user.id || user._id || "N/A" },
          ],
        },
        onSuccess: async (remitaResponse) => {
          try {
            
            await axios.get(
              `/api/wallet/fund/verify?reference=${data.reference}&rrr=${data.rrr}&amount=${amount}`
            );

            // Go to success receipt
            navigate("/wallet/fund/receipt", {
              state: {
                status: "success",
                amount: Number(amount),
                walletBalance,
                newBalance: walletBalance + Number(amount),
                reference: data.reference,
                rrr: data.rrr,
                transactionId: remitaResponse.transactionId,
              },
            });
          } catch (verifyErr) {
            console.error("Verification failed:", verifyErr);
            setErrorMessage("Payment succeeded but verification failed. Contact support.");
            navigate("/wallet/fund/receipt", {
              state: {
                status: "failed",
                amount: Number(amount),
                walletBalance,
                message: "Payment processed but could not be verified",
              },
            });
          }
        },
        onError: (err) => {
          setErrorMessage(err.message || "Payment failed. Please try again.");
          navigate("/wallet/fund/receipt", {
            state: {
              status: "failed",
              amount: Number(amount),
              walletBalance,
              message: err.message || "Payment declined",
            },
          });
        },
        onClose: () => {
          if (!errorMessage) {
            setErrorMessage("You closed the payment window.");
          }
        },
      });

      paymentEngine.showPaymentWidget();
    } catch (err) {
      console.error("Remita funding error:", err);
      setErrorMessage(err.message || "Failed to start payment process");
      navigate("/wallet/fund/receipt", {
        state: {
          status: "failed",
          amount: Number(amount),
          walletBalance,
          message: err.message || "Could not initialize payment",
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-[#2F2D2E] flex flex-col">
      <DashboardNavbar />

      <main className="w-full max-w-md mx-auto px-5 py-12">
        <div className="flex items-center gap-4 mb-10 mt-15">
          <Button
            onClick={() => navigate("/wallet/fund")}
            className="bg-gray-900 border border-gray-700 text-white hover:bg-[#009D50] rounded-lg h-10 w-10 flex items-center justify-center"
          >
            <FaArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-white bg-[#2F2D2E] rounded-lg p-4 text-center flex-1">
            Confirm Funding
          </h1>
        </div>

        <div className="bg-[#2F2D2E] p-7 rounded-xl shadow-xl space-y-7">
          <div className="text-center">
            <p className="text-gray-300 text-lg mb-2">Funding Amount</p>
            <p className="text-5xl font-bold text-green-400">
              ₦{Number(amount).toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-800 p-5 rounded-lg space-y-3 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Deposit Amount</span>
              <span>₦{Number(amount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Service Charge</span>
              <span className="text-orange-400">₦{SERVICE_CHARGE.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-white font-semibold pt-2 border-t border-gray-700">
              <span>Total Debit</span>
              <span>₦{totalCharge.toLocaleString()}</span>
            </div>
            <div className="h-px bg-gray-700 my-1" />
            <div className="flex justify-between text-gray-300">
              <span>Current Wallet Balance</span>
              <span>₦{Number(walletBalance).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-green-400 font-semibold">
              <span>Amount Credited to Wallet</span>
              <span>+₦{Number(amount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-gray-700">
              <span>New Balance</span>
              <span>₦{(Number(walletBalance) + Number(amount)).toLocaleString()}</span>
            </div>
          </div>

          {errorMessage && (
            <p className="text-red-400 text-center text-sm py-2">{errorMessage}</p>
          )}

          <div className="pt-4">
            <Button
              onClick={handlePayWithRemita}
              disabled={isProcessing}
              className="w-full bg-[#009D50] hover:bg-[#007A3E] text-white h-12 text-lg font-semibold disabled:opacity-50"
            >
              {isProcessing ? "Processing Payment..." : "Pay with Remita"}
            </Button>
          </div>

          <p className="text-xs text-gray-400 text-center">
            Secured by Remita • Supports Cards, Bank Transfer, USSD
          </p>
        </div>
      </main>
    </div>
  );
}